import mediaQuery from 'css-mediaquery';
import { createTheme, ThemeOptions, responsiveFontSizes } from '@mui/material/styles';
import { COUNTRY } from '@mango-engineering/maknet-client-sdk/lib/shop/types';

const createSsrMatchMedia = (width: string) => (query: string) => ({
  matches: mediaQuery.match(query, { width }),
});

const themeConfig: (deviceWidth: string, country: COUNTRY) => ThemeOptions = (
  deviceWidth: string,
  country: COUNTRY,
) => {
  // eslint-disable-next-line no-useless-computed-key
  return {
    palette: {
      mode: 'light',
      primary: {
        main: country === COUNTRY.CAMBODIA ? '#0066B9' : '#F22643',
        light: country === COUNTRY.CAMBODIA ? '#58a2e8' : '#F77D8E',
        dark: country === COUNTRY.CAMBODIA ? '#1d4782' : '#9D2538',
      },
      secondary: {
        main: '#212124',
        light: '#4D4D4F',
        dark: '#171719',
      },
      error: {
        main: '#BA1A1A',
        light: '#8F414D',
        dark: '#500C17',
      },
      warning: {
        main: '#F26233',
        light: '#F4815B',
        dark: '#A94423',
      },
      info: {
        main: '#1BAFF2',
        light: '#48BFF4',
        dark: '#127AA9',
      },
      success: {
        main: '#00BF26',
        light: '#D0FFD9',
        dark: '#025C26',
      },
      iconColors: {
        background: country === COUNTRY.CAMBODIA ? '#D4E6F7' : '#FFD8D9',
        primary: country === COUNTRY.CAMBODIA ? '#0066B9' : '#E82026',
        secondary: country === COUNTRY.CAMBODIA ? '#58A2E8' : '#F73B41',
      },
      grey: {
        50: '#F4F4F4',
        100: '#E8E8E8',
        200: '#C7C7C7',
        300: '#8E8E8E',
        400: '#6B6B6B',
        900: '#212124',
      },
      divider: '#E8E8E8',
      common: {
        mds_global_color_teal: '#08C6BA',
        mds_global_color_yellow: '#FFCF00',
        mds_global_color_yellow_2: '#5C4B00',
        mds_global_color_yellow_3: '#FBF3D5',
        mds_global_color_yellow_4: '#564A04',
        mds_global_color_yellow_5: '#E6D345',
        mds_global_color_yellow_6: '#FFEA71',
        mds_global_color_yellow_7: '#FFE000',
        mds_global_background_yellow: '#FFF9E0',
        mds_global_background_yellow_5: '#FFF7D4',
        mds_global_color_red_5: '#FFDADF',
        mds_global_color_red_6: '#731221',
        mds_global_color_red_7: '#D21E3A',
        mds_global_color_red_8: '#E84861',
        mds_global_color_red_9: '#F8505A',
        mds_global_color_red_10: '#F226A0',
        mds_global_color_red_11: '#D84018',
        mds_global_color_red_12: '#FF2C00',
        mds_global_color_red_13: '#FF6761',
        mds_global_color_red_30: '#FD1E1B',
        mds_global_color_blue_20: '#ECF8FE',
        mds_global_color_blue_50: '#5798F5',

        mds_global_color_orange_20: '#FEF0EC',
        mds_global_color_orange_30: '#FFEBE5',
        mds_global_color_orange_80: '#FFFAED',
        mds_global_color_orange_90: '#FFAF37',
        mds_global_color_green_70: '#F7FCF8',
        mds_global_color_orange_40: '#FFB45B',
        mds_global_color_red_15: '#FEE9EC',
        mds_global_color_red_25: country === COUNTRY.CAMBODIA ? '#0066B9' : '#E82026',
        mds_global_color_red_14: '#CC0000',
        mds_global_color_red_50: '#E82026',
        mds_global_color_green_20: '#D0FFD9',
        mds_global_color_green_30: '#026429',
        mds_global_color_green_40: '#0B8A1C',
        mds_global_color_transparent: 'transparent',
        mds_global_color_white: '#FFFFFF',
        mds_global_color_orange: '#FF6432',
        mds_global_background_red: '#FEE9EC',
        mds_global_background_green: '#EBFFEF',
        mds_global_color_purple: '#6109FF',
        mds_global_color_purple_light: '#EEE6FF',
        mds_global_background_orange: '#FFECE6',
        mds_global_background_gray: '#3e323380',
        mds_global_background_gray_1: '#e8e8e8',
        mds_global_background_gray_2: '#D4D4D8',
        mds_global_background_red_1: '#FF0000',
        mds_global_color_purple_1: '#8057F6',
        mds_global_color_orange_10: '#FF8F00',
        mds_global_color_light_blue: '#EBF9FF',
        mds_global_color_dark_blue: '#004BAO',
        mds_global_color_green_50: '#E2FFED',
        mds_global_color_green_60: '#B3FFCF',
        mds_global_color_green_80: '#006013',
        mds_global_color_green_90: '#98FFBD',
        mds_global_color_green_100: '#ABFFCA',
        mds_global_color_blue_10: '#2F8AF4',
        mds_global_color_blue_30: '#1274E6',
        mds_global_color_blue_35: '#0071CD',
        mds_global_color_blue_45: '#4DA7F2',
        mds_global_color_blue_55: '#2D95EC',
        mds_global_color_pink_10: '#FF3969',
        mds_global_color_pink_20: '#FF4471',
        mds_global_color_grey_10: '#EEEEEE',
        mds_global_color_brown_10: '#805831',
        mds_global_color_black: '#000000',
        mds_global_color_black_1: '#18181B',
        mds_global_color_black_20: '#18181B',
        mds_global_color_grey_5: '#F4F4F5',
        mds_global_color_grey_15: '#E4E4E7',
        mds_global_color_grey_20: '#FAFAFA',
        mds_global_color_grey_25: '#D4D4D8',
        mds_global_color_grey_35: '#F0F6FD',
        mds_global_color_grey_40: '#797474',
        mds_global_color_grey_50: '#333333',
        mds_global_color_grey_60: '#3F3F46',
        mds_global_color_grey_70: '#71717A',
        mds_global_color_black_25: '#1A1A1A',
        mds_global_color_grey_100: '#F4F4F5',
        mds_global_color_orange_55: '#FFF2E7',
      },
    },
    typography: {
      fontFamily: 'inherit',
      fontSize: 16,
      fontWeightRegular: 400,
      fontWeightLight: 300,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      allVariants: {
        fontFamily: 'inherit',
      },
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '2.5rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.2rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        lineHeight: 1.25,
        fontWeight: 500,
      },
      subtitle1: {
        lineHeight: 1.5,
      },
      subtitle2: {
        fontWeight: 400,
        lineHeight: 1.25,
      },
      body1: {
        lineHeight: 1.35,
        fontSize: '1rem',
      },
      body2: {
        lineHeight: 1.35,
        fontSize: '1rem',
        fontWeight: 400,
      },
      button: {
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
      },
      caption: {
        fontSize: '0.8rem',
        lineHeight: 1.6,
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 4,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        mobile: 280,
        tablet: 768,
        desktop: 1204,
      },
    },
    mixins: {
      toolbar: {
        height: '0.5rem',
        width: '1rem',
      },
    },
    components: {
      MuiUseMediaQuery: {
        defaultProps: {
          // This will allow useMediaQuery to use this during SSR
          ssrMatchMedia: createSsrMatchMedia(deviceWidth),
          noSsr: false,
        },
      },
      MuiCssBaseline: {
        styleOverrides: () => ({
          html: { height: '100%', overflow: 'auto' },
          '::-webkit-scrollbar': {
            '-webkit-appearance': 'none',
            width: '6px',
            height: '9px',
          },
          '::-webkit-scrollbar-thumb': {
            borderRadius: '6px',
            backgroundColor: 'rgba(142, 142, 142, 1)',
            boxshadow: '0 0 1px rgba(255, 255, 255, 0.5)',
          },
          '#ot-sdk-btn-floating': {
            display: 'none',
          },
          '#onetrust-pc-sdk .ot-tgl input:checked+.ot-switch .ot-switch-nob': {
            backgroundColor: `${country === COUNTRY.CAMBODIA ? '#0066B9' : '#E82026'} !important`,
          },
          '#onetrust-consent-sdk': {
            display: 'none',
          },
        }),
      },
    },
  };
};

export const AppThemeConfig = (deviceWidth: string, country: COUNTRY) =>
  responsiveFontSizes(createTheme(themeConfig(deviceWidth, country)));
// TODO: Remove this export once all the hard coded and direct use of `DefaultThemeOptions` is fixed in all over the app eg: src/components/molecules/badge/styles/badge.styles.tsx
// Developers need to follow the MUI theme standard rule to use the theme using ({theme}) => ({..css})
// Also need to remove the non MUI styled rule use case, like somewhere using .css, styled(div)`...css`, standard use is: comp = styled(Box)(({theme}) => ({...css}))
// Also need to remove the styled function import to be always use from  import {styled} "@mui/material/*", currently multiple use of the import from @emotion, styled libs which is causing many issues
export const DefaultThemeOptions = responsiveFontSizes(createTheme(themeConfig('1200px', COUNTRY.THAILAND)));
export default DefaultThemeOptions;
